// Live call state for one project.
//
// The row we subscribe to lives in `public.project_calls`, which never contains
// provider identifiers. Every row is still run through `stripProviderFields`
// before it reaches React state as defence in depth.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import {
  isLive,
  isRingExpired,
  stripProviderFields,
  type CallRow,
} from "@/lib/calls.core";
import { fetchCallsConfig } from "@/lib/calls";

export function useCallsEnabled() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    void fetchCallsConfig().then((res) => {
      if (!cancelled) setEnabled(res.ok ? !!res.data?.enabled : false);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return enabled;
}

export function useProjectCall(projectId: string) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [call, setCall] = useState<CallRow | null>(null);
  const [tick, setTick] = useState(0);
  const callRef = useRef<CallRow | null>(null);

  const apply = useCallback((row: Record<string, unknown> | null) => {
    const next = row ? (stripProviderFields(row) as unknown as CallRow) : null;
    callRef.current = next && isLive(next.status) ? next : next;
    setCall(next);
  }, []);

  // Initial load of any still-live call for this project.
  useEffect(() => {
    if (!projectId || !userId) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from("project_calls")
        .select("*")
        .eq("project_id", projectId)
        .in("status", ["provisioning", "ringing", "accepted", "connecting", "active"])
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!cancelled) apply((data as Record<string, unknown> | null) ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, userId, apply]);

  // Realtime: RLS keeps this scoped to projects the user participates in.
  useEffect(() => {
    if (!projectId) return;
    const channel = supabase
      .channel(`project-calls:${projectId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "project_calls",
          filter: `project_id=eq.${projectId}`,
        },
        (payload) => {
          const row = payload.new as Record<string, unknown> | null;
          if (!row) return;
          const current = callRef.current;
          // Ignore stale rows for a call we already replaced.
          if (current && row["id"] !== current.id && !isLive(String(row["status"]) as never)) return;
          apply(row);
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [projectId, apply]);

  // Local ring countdown so the callee UI closes itself on expiry.
  useEffect(() => {
    if (!call || !isLive(call.status)) return;
    const id = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [call]);

  const state = useMemo(() => {
    if (!call || !userId) {
      return { call: null, role: null as null | "caller" | "callee", ringExpired: false };
    }
    const role = call.initiator_user_id === userId ? "caller" : "callee";
    const ringExpired = call.status === "ringing" && isRingExpired(call.ring_expires_at);
    return { call, role: role as "caller" | "callee", ringExpired };
    // `tick` intentionally re-derives ringExpired each second.
     
  }, [call, userId, tick]);

  return { ...state, setCall: apply, currentUserId: userId };
}
